import { useCookies } from '@vueuse/integrations/useCookies';
import AuthenticationService from '../core/shared/authentication/authentication.service';
import { type Router } from 'vue-router';
import MeService from '@/core/shared/me/me.service';
import { EmployerFeatureService } from '@/core/shared/employer-feature/employer-feature.service';
import SubscriptionService from '@/core/shared/subscription/subscription.service';
import JobTitleService from '@/core/shared/job-title/job-title.service';
import TaxonomyService from '@/core/shared/taxonomy/taxonomy.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { classifyBootstrapError } from '@/core/shared/errors/classify-bootstrap-error.util';
import { InternalError } from '@/core/shared/errors/internal.error';
import { useBootstrapStore } from '@/core/shared/bootstrap/bootstrap.store';
import {
  CONNECT_CONSENT_PATH,
  CONSENT_INTERACTION_QUERY_KEY,
  consentInteractionFromLocation,
  saveConsentReturn,
  takeConsentReturn,
} from '@/core/shared/connector-consent/consent-return.util';

export default async (router: Router): Promise<void> => {
  const cookies = useCookies();
  const authenticationService = new AuthenticationService();
  const meService = new MeService();
  const employerFeatureService = new EmployerFeatureService();
  const subscriptionService = new SubscriptionService();
  const jobTitleService = new JobTitleService();
  const taxonomyService = new TaxonomyService();

  const authTokenCookieName = import.meta.env.VITE_AUTH_TOKEN_COOKIE_NAME;
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const refreshTokenFromUrl = urlParams.get('refreshToken');
  const refreshTokenExpiresAtFromUrl = urlParams.get('refreshTokenExpiresAt');
  const ui2SelectedEmployerId = urlParams.get('ui2SelectedEmployerId');
  const route = window.location.pathname;

  // A consent request survives the trip to ff-ui-v2's login: saved before any redirect, replayed when
  // the login hands the session back on the root path.
  const consentInteraction = consentInteractionFromLocation(route, window.location.search);
  if (consentInteraction) {
    saveConsentReturn(consentInteraction);
  }

  // Keep route reference if present. Remove handoff params from query string.
  urlParams.delete('token');
  urlParams.delete('refreshToken');
  urlParams.delete('refreshTokenExpiresAt');
  urlParams.delete('ui2SelectedEmployerId');

  // Scrub the credentials from the address bar immediately: tokens must not
  // linger in browser history, and a later reload (e.g. from the bootstrap
  // error gate) must not re-apply a stale refresh token over a rotated one —
  // the server's reuse detection would kill the valid session.
  const scrubbedSearch = urlParams.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${route}${scrubbedSearch ? `?${scrubbedSearch}` : ''}${window.location.hash}`,
  );

  const query = {} as Record<string, string | string[]>;
  for (const [key, value] of urlParams.entries()) {
    if (!query[key]) {
      query[key] = value;
    } else {
      const keyValue = query[key];
      if (Array.isArray(keyValue)) {
        keyValue.push(value);
      } else {
        query[key] = [keyValue, value];
      }
    }
  }

  cookies.remove(authTokenCookieName);

  try {
    authenticationService.setIdToken(token);

    // UI2 hands off the refresh token alongside the JWT for fresh logins.
    // Persist it before any auth-dependent fetches.
    if (refreshTokenFromUrl && refreshTokenExpiresAtFromUrl) {
      const expiresAt = Number(refreshTokenExpiresAtFromUrl);
      if (Number.isFinite(expiresAt)) {
        authenticationService.setRefreshToken(refreshTokenFromUrl, expiresAt);
      }
    }

    await authenticationService.ensureFreshIdToken();

    // Guest users and post-expiry sessions land here on every visit;
    // bouncing through the catch block would log each one to Sentry.
    if (!authenticationService.isSessionRecoverable) {
      await authenticationService.logout();
      meService.redirectToUI2('/employer/auth/logout');
      return;
    }

    // ensureFreshIdToken swallows transient /auth/token failures. If the idToken
    // is still unusable here while the refresh token is valid, the refresh
    // failed transiently; proceeding to fetchMe would 401 on the stale idToken
    // and trip the global logout, destroying a valid session on a blip. Surface
    // it as a transient failure so the catch routes to the reload gate.
    if (!authenticationService.isAuthenticated) {
      throw new InternalError('idToken refresh failed during bootstrap');
    }

    authenticationService.initResumeListeners();

    const pendingConsent =
      consentInteraction ?? (route === '/' && token ? takeConsentReturn() : undefined);
    if (pendingConsent) {
      await bootstrapConsentOnly(router, pendingConsent);
      return;
    }

    await meService.fetchMe();

    if (ui2SelectedEmployerId) {
      meService.setSelectedEmployerId(Number(ui2SelectedEmployerId));
    }
    meService.selectDefaultEmployerIfUnset();

    // A user tied to zero employers can never load the dashboard, so the
    // reload gate would be a dead end for them ("reload" can't conjure an
    // employer). Treat it like any other unusable session.
    if (!meService.getSelectedEmployerId()) {
      await authenticationService.logout();
      meService.redirectToUI2('/employer/auth/logout');
      return;
    }

    await authenticationService.refreshAccessToken();

    await meService.fetchAndSetEmployer();
    await employerFeatureService.fetchEmployerFeatures();
    await subscriptionService.fetchSubscription();

    await Promise.all([
      jobTitleService.fetchJobTitles(),
      meService.fetchEmployerUsers(),
      taxonomyService.reload().catch((error: unknown) => {
        ErrorService.captureException(error);
      }),
    ]);
  } catch (error: unknown) {
    ErrorService.captureException(error);

    if (classifyBootstrapError(error) === 'auth') {
      await authenticationService.logout();
      meService.redirectToUI2('/employer/auth/logout');
      return;
    }

    // Outages and network blips can't be fixed by logging out — that would
    // destroy a valid session and bounce the user through UI2's false
    // "plan expired" screen. Keep the session and show a reload gate instead;
    // recovery stays user-initiated so a persistent outage can't reload-loop.
    useBootstrapStore().hasBlockingError = true;
    return;
  }

  await router.replace({ path: route, query, hash: window.location.hash });
};

// Consent is the user's own, across companies: no selected employer, employer token, subscription or
// job titles. A user with no memberships, or a dashboard dependency that is down, still reaches it.
async function bootstrapConsentOnly(router: Router, interaction: string): Promise<void> {
  // The router's own first navigation (the handoff URL, redirected to the jobs page) may still be
  // settling; a navigation issued before it finishes is the one that gets cancelled, and the consent
  // route's lazy chunk makes that window real.
  await router.isReady();
  await router.replace({
    path: CONNECT_CONSENT_PATH,
    query: { [CONSENT_INTERACTION_QUERY_KEY]: interaction },
  });
}
